import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { requestEnableIduraEidTemplate } from 'apollo/templates/user/mutation.graphql';
import { AuthMethod, User } from 'models/user';
import Personnummer from 'personnummer';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import EidMark from '../eid-mark.component';

type EnableEidDialogProps = {
    /** The e-ID the user picked. Null closes the dialog — there is no "no method" state. */
    method: AuthMethod | null;
    onClose: () => void;
    user: User;
};

// DD-3513 — Sweden is the only country that asks for a number.
//
// It keeps matching on the personal identity number so everyone who enrolled through
// GrandID carries over without re-enrolling, and the backend checks the number the
// e-ID authenticates with against the one typed here. Norway, Denmark and Finland are
// matched on a pseudonym that only exists once the user has authenticated, so for them
// the authentication *is* the binding and there is nothing to type.
export const EID_METHODS = [
    { method: AuthMethod.BANKID_SE, labelKey: 'user-settings:EID_BANKID_SE' },
    { method: AuthMethod.BANKID_NO, labelKey: 'user-settings:EID_BANKID_NO' },
    { method: AuthMethod.MITID_DK, labelKey: 'user-settings:EID_MITID_DK' },
    { method: AuthMethod.FTN_FI, labelKey: 'user-settings:EID_FTN_FI' },
];

const EnableEidDialog: FC<EnableEidDialogProps> = ({ method, onClose, user }: EnableEidDialogProps) => {
    const { t: translate } = useTranslation(['user-settings', 'common']);
    const [isLoading, setIsLoading] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const [pin, setPin] = useState(user.personalIdentityNumber ?? '');
    const [isPinValid, setIsPinValid] = useState(false);

    const requiresPersonalIdentityNumber = method === AuthMethod.BANKID_SE;
    const labelKey = `user-settings:EID_${method}`;

    const [requestEnableIduraEid] = useMutation(requestEnableIduraEidTemplate, {
        onCompleted: ({ requestEnableIduraEid }) => {
            if (requestEnableIduraEid) {
                window.location.assign(requestEnableIduraEid);
            }
        },
        onError(error) {
            setIsLoading(false);
            showBoundary(error);
        },
    });

    const enableEid = () => {
        setIsLoading(true);
        requestEnableIduraEid({
            variables: {
                method,
                personalIdentityNumber: requiresPersonalIdentityNumber ? new Personnummer(pin).format(true) : undefined,
            },
        });
    };

    useEffect(() => {
        setIsPinValid(Personnummer.valid(pin));
    }, [pin]);

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={Boolean(method)}
            onClose={onClose}
            title={method ? translate(labelKey) : ''}
            actions={
                <LoadingButton
                    variant="contained"
                    onClick={() => enableEid()}
                    loading={isLoading}
                    disabled={requiresPersonalIdentityNumber && !isPinValid}
                    data-test="account-settings.enable-eid-submit"
                >
                    {translate('user-settings:ENABLE_EID_CONFIRM', {
                        brand: translate(`user-settings:EID_BRAND_${method}`),
                        // The Finnish brand contains a slash; escaping would show &#x2F;.
                        interpolation: { escapeValue: false },
                    })}
                </LoadingButton>
            }
        >
            <Box display="flex" width="100%">
                {method ? <EidMark method={method} alt={translate(labelKey)} /> : undefined}
                <Box mb={1} sx={{ width: '100%' }}>
                    <Box mb={2}>{translate('user-settings:ENABLE_EID_INSTRUCTIONS')}</Box>
                    {requiresPersonalIdentityNumber ? (
                        <Box>
                            <TextField
                                fullWidth
                                value={pin}
                                onChange={(event) => setPin(event.target.value)}
                                label={translate('common:PERSONAL_IDENTITY_NUMBER')}
                                data-test="account-settings.eid-personal-identity-number"
                            />
                        </Box>
                    ) : (
                        <Alert severity="info">{translate('user-settings:ENABLE_EID_NO_NUMBER_NEEDED')}</Alert>
                    )}
                    <Box mt={2}>
                        <Alert severity="warning">{translate('user-settings:ENABLE_EID_REPLACES_PASSWORD')}</Alert>
                    </Box>
                </Box>
            </Box>
        </DHDialog>
    );
};

export default EnableEidDialog;
